import { Injectable } from 'framework/di';

import type { Product } from '../../providers/shopify/models';
import { AppService } from '../app/app.service';
import type { CartAddItem } from './cart.api';
import { CartAPI } from './cart.api';
import type { AddToCartItem, AddToCartResult, CartModalData, CartRenderConfig } from './cart.model';
import { CartSelectors } from './cart.selectors';
import type { CartDrawerRecommendationTemplateData } from './cart-drawer-recommendation/model';
import { CartDrawerRecommendationService } from './cart-drawer-recommendation/service';

/**
 * Cart service managing cart operations and business logic
 *
 * Provides cart functionality including:
 * - Adding products to cart (single or multi-product)
 * - Cart quantity management with queue to prevent race conditions
 * - Cart modal rendering with template customization
 * - Post-add-to-cart action handling (redirect, drawer, etc.)
 * - Cart backup/restore functionality
 *
 * Extension points for Technical Support customization:
 * - {@link handlePostAddToCartAction} - Custom redirect/drawer behavior after adding items
 * - {@link redirectToCartPage} - Custom cart page redirect logic
 * - {@link getDefaultCartConfig} - Shop-specific cart rendering defaults
 * - {@link processCartItem} - Custom cart item formatting and enrichment
 * - {@link getCartTemplate} - Custom cart template loading
 * - {@link validateAddToCartItem} - Custom validation rules for cart items
 * - {@link enrichCartData} - Add shop-specific data to cart responses
 * - {@link handleCartError} - Custom error handling and user feedback
 *
 * @example Basic usage
 * ```typescript
 * const cartService = container.resolve(CartService);
 *
 * // Add single product
 * await cartService.addToCart({
 *   productId: '12345',
 *   quantity: 2
 * });
 *
 * // Add multiple products
 * await cartService.addToCart({
 *   items: [
 *     { id: '12345', quantity: 1 },
 *     { id: '67890', quantity: 2 }
 *   ]
 * });
 * ```
 *
 * @example Technical Support customization
 * ```typescript
 * class CustomCartService extends CartService {
 *   // Custom post-add behavior: show notification instead of redirect
 *   protected async handlePostAddToCartAction(): Promise<void> {
 *     const { enableCart, cartNotification } = this.appService.themeSettings.cart;
 *
 *     if (enableCart) {
 *       // Show cart drawer
 *       this.showCartDrawer();
 *     } else if (cartNotification) {
 *       // Show success notification
 *       this.showSuccessNotification();
 *     } else {
 *       // Default: redirect to cart page
 *       this.redirectToCartPage();
 *     }
 *   }
 *
 *   // Custom cart item processing: add gift wrapping options
 *   protected processCartItem(item: Product, showCurrencyCodes: boolean) {
 *     const processed = super.processCartItem(item, showCurrencyCodes);
 *
 *     // Add gift wrapping info if available
 *     if (item.properties?.gift_wrap) {
 *       processed.giftWrapping = {
 *         enabled: true,
 *         message: item.properties.gift_message,
 *       };
 *     }
 *
 *     return processed;
 *   }
 *
 *   // Custom validation: enforce minimum order amount
 *   protected async validateAddToCartItem(item: AddToCartItem): Promise<boolean> {
 *     const baseValid = await super.validateAddToCartItem(item);
 *     if (!baseValid) return false;
 *
 *     const minOrderAmount = this.appService.getConfig().minOrderAmount;
 *     if (minOrderAmount) {
 *       const cartData = await this.getCartData();
 *       const newTotal = cartData.total_price + (item.price * item.quantity);
 *
 *       if (newTotal < minOrderAmount) {
 *         this.showMinOrderAmountError(minOrderAmount);
 *         return false;
 *       }
 *     }
 *
 *     return true;
 *   }
 * }
 * ```
 */
@Injectable
export class CartService {
  /** Queue for cart updates to prevent race conditions */
  private updateQueue: Promise<unknown> = Promise.resolve();

  constructor(
    private cartAPI: CartAPI,
    private cartSelectors: CartSelectors,
    private appService: AppService,
    protected cartDrawerRecommendationService: CartDrawerRecommendationService
  ) {}

  /**
   * Add product(s) to cart with validation and error handling
   *
   * Supports both single product and multi-product additions.
   * Handles cart count updates, post-add actions, and error scenarios.
   *
   * Extension points:
   * - Override {@link validateAddToCartItem} to add custom validation rules
   * - Override {@link handlePostAddToCartAction} to customize post-add behavior
   * - Override {@link handleCartError} to customize error handling
   *
   * @param item - Cart item configuration (single product or multi-product)
   * @returns Promise resolving to operation result with success status and optional response/error
   *
   * @example Add single product
   * ```typescript
   * const result = await cartService.addToCart({
   *   productId: '12345',
   *   quantity: 2,
   *   properties: { gift_message: 'Happy Birthday!' }
   * });
   *
   * if (result.success) {
   *   console.log('Added to cart:', result.response);
   * } else {
   *   console.error('Failed:', result.error);
   * }
   * ```
   *
   * @example Add multiple products
   * ```typescript
   * const result = await cartService.addToCart({
   *   items: [
   *     { id: '12345', quantity: 1 },
   *     { id: '67890', quantity: 2, selling_plan: 'sub_123' }
   *   ]
   * });
   * ```
   *
   * @throws {Error} When cart API operations fail or validation errors occur
   */
  async addToCart(item: AddToCartItem): Promise<AddToCartResult> {
    try {
      // Validate item before adding (extension point)
      const isValid = await this.validateAddToCartItem(item);
      if (!isValid) {
        return {
          success: false,
          error: 'Cart item validation failed',
        };
      }

      let response: Response;

      if (item.items && Array.isArray(item.items)) {
        response = await this.cartAPI.addMultiProductToCart(item.items);
      } else if (item.productId) {
        const { productId, quantity = 1, selling_plan = null, properties = {} } = item;
        response = await this.cartAPI.add(productId, quantity, selling_plan, properties);
      } else {
        throw new Error('Invalid cart item format: missing productId or items array');
      }

      if (response.status === 200) {
        const cartData = await this.cartAPI.getAll();
        this.cartSelectors.updateCartCount(cartData.item_count ?? 0);

        await this.handlePostAddToCartAction();

        const responseData = await response.json();
        // Allow shops to enrich response with custom data (extension point)
        const enrichedResponse = await this.enrichCartData(responseData, item);

        return { success: true, response: enrichedResponse };
      } else {
        const error = `Failed to add to cart: ${response.status}`;
        return this.handleCartError(error, 'add_to_cart_failed', item);
      }
    } catch (error) {
      return this.handleCartError(
        error instanceof Error ? error.message : 'Unknown error',
        'add_to_cart_exception',
        item
      );
    }
  }

  /**
   * Validate cart item before adding to cart
   * Override to implement shop-specific validation rules
   *
   * Common customization scenarios:
   * - Enforce minimum/maximum quantities
   * - Check inventory availability
   * - Validate custom properties
   * - Apply business rules (subscription eligibility, etc.)
   *
   * @param item - Cart item to validate
   * @returns Promise resolving to true if item passes validation, false otherwise
   *
   * @example Minimum quantity validation
   * ```typescript
   * protected async validateAddToCartItem(item: AddToCartItem): Promise<boolean> {
   *   const baseValid = await super.validateAddToCartItem(item);
   *   if (!baseValid) return false;
   *
   *   const minQty = this.appService.getConfig().minQuantity || 1;
   *   if (item.quantity && item.quantity < minQty) {
   *     this.showError(`Minimum quantity is ${minQty}`);
   *     return false;
   *   }
   *
   *   return true;
   * }
   * ```
   */
  protected async validateAddToCartItem(item: AddToCartItem): Promise<boolean> {
    // Basic validation - override to add shop-specific rules
    if (item.items) {
      return Array.isArray(item.items) && item.items.length > 0;
    }

    return Boolean(item.productId);
  }

  /**
   * Enrich cart data with shop-specific information
   * Override to add custom fields, metadata, or transformations
   *
   * Common customization scenarios:
   * - Add product recommendations
   * - Include loyalty points information
   * - Add estimated delivery dates
   * - Add gift wrapping options
   * - Add cross-sell/upsell data
   *
   * @param cartData - Raw cart response data from Shopify Cart API
   * @param addedItem - Cart item that was just added to provide context for enrichment
   * @returns Promise resolving to enriched cart data with shop-specific additions
   *
   * @example Add loyalty points
   * ```typescript
   * protected async enrichCartData(cartData: any, addedItem: AddToCartItem) {
   *   const enriched = await super.enrichCartData(cartData, addedItem);
   *
   *   // Add loyalty points calculation
   *   const loyaltyAPI = this.getLoyaltyAPI();
   *   const points = await loyaltyAPI.calculatePoints(cartData.total_price);
   *
   *   return {
   *     ...enriched,
   *     loyalty: {
   *       pointsEarned: points,
   *       tierStatus: await loyaltyAPI.getTierStatus(),
   *     },
   *   };
   * }
   * ```
   */
  protected async enrichCartData(cartData: unknown, addedItem: AddToCartItem): Promise<unknown> {
    // Default: return data as-is
    // Override to add shop-specific enrichments
    return cartData;
  }

  /**
   * Handle cart operation errors with custom logging and user feedback
   * Override to customize error handling, user notifications, and tracking
   *
   * Common customization scenarios:
   * - Show user-friendly error messages
   * - Track errors in analytics
   * - Send error notifications to support team
   * - Implement retry logic for transient errors
   * - Trigger fallback behaviors
   *
   * @param error - Error message describing what went wrong
   * @param errorType - Error type identifier for categorization and tracking
   * @param context - Additional context about the failed operation (cart item, request details)
   * @returns AddToCartResult object with success=false and error message
   *
   * @example Custom error tracking and notifications
   * ```typescript
   * protected handleCartError(
   *   error: string,
   *   errorType: string,
   *   context?: unknown
   * ): AddToCartResult {
   *   // Log to external error tracking service
   *   this.trackError({
   *     type: errorType,
   *     message: error,
   *     context,
   *     timestamp: Date.now(),
   *   });
   *
   *   // Show user-friendly notification
   *   this.showErrorNotification(this.getUserFriendlyMessage(errorType));
   *
   *   // Return structured error
   *   return super.handleCartError(error, errorType, context);
   * }
   *
   * private getUserFriendlyMessage(errorType: string): string {
   *   const messages = {
   *     'add_to_cart_failed': 'Unable to add item to cart. Please try again.',
   *     'inventory_unavailable': 'This item is currently out of stock.',
   *     'invalid_quantity': 'Please enter a valid quantity.',
   *   };
   *   return messages[errorType] || 'An error occurred. Please try again.';
   * }
   * ```
   */
  protected handleCartError(error: string, errorType: string, context?: unknown): AddToCartResult {
    // Log error with context
    this.appService.logger.error(`Cart error [${errorType}]:`, error, context);

    // Return error result
    return { success: false, error };
  }

  /**
   * Handle post add-to-cart actions (redirect, drawer, notification, etc.)
   * Override to customize behavior after successfully adding items to cart
   *
   * Common customization scenarios:
   * - Show cart drawer/modal instead of redirect
   * - Display success notification
   * - Trigger upsell/cross-sell modal
   * - Update mini cart display
   * - Track add-to-cart events in analytics
   *
   * Default behavior:
   * - If cart drawer is enabled: do nothing (let drawer handle it)
   * - If cart drawer is disabled: redirect to cart page
   *
   * @example Show cart drawer
   * ```typescript
   * protected async handlePostAddToCartAction(): Promise<void> {
   *   const { enableCart, showNotification } = this.appService.themeSettings.cart;
   *
   *   if (enableCart) {
   *     // Show cart drawer
   *     await this.showCartDrawer();
   *   } else if (showNotification) {
   *     // Show success notification without redirect
   *     this.showSuccessNotification('Item added to cart!');
   *   } else {
   *     // Default: redirect to cart page
   *     this.redirectToCartPage();
   *   }
   * }
   * ```
   *
   * @example Track analytics and show upsell
   * ```typescript
   * protected async handlePostAddToCartAction(): Promise<void> {
   *   // Track add-to-cart event
   *   await this.trackAddToCartEvent();
   *
   *   // Show upsell recommendations
   *   const showUpsell = this.appService.getConfig().upsell?.enabled;
   *   if (showUpsell) {
   *     await this.showUpsellModal();
   *     return; // Don't redirect if showing upsell
   *   }
   *
   *   // Default behavior
   *   await super.handlePostAddToCartAction();
   * }
   * ```
   *
   * @returns Promise that resolves when post-add action is complete
   */
  protected async handlePostAddToCartAction(): Promise<void> {
    try {
      const { enableCart } = this.appService.themeSettings.cart;

      if (!enableCart) {
        this.redirectToCartPage();
      }
    } catch (error) {
      this.appService.logger.error('Failed to handle post add-to-cart action:', error);
      // Fallback: redirect to cart page on error (safe default behavior)
      this.redirectToCartPage();
    }
  }

  /**
   * Redirect to cart page with proper locale handling
   * Override for custom redirect logic or additional behavior
   *
   * Common customization scenarios:
   * - Add query parameters to cart URL
   * - Redirect to custom checkout page
   * - Add analytics tracking before redirect
   * - Show loading indicator during redirect
   *
   * @example Redirect to custom checkout
   * ```typescript
   * protected redirectToCartPage(): void {
   *   const useCustomCheckout = this.appService.getConfig().customCheckout;
   *
   *   if (useCustomCheckout) {
   *     const checkoutUrl = this.buildCustomCheckoutUrl();
   *     this.safeRedirect(checkoutUrl);
   *   } else {
   *     super.redirectToCartPage();
   *   }
   * }
   * ```
   *
   * @example Add tracking parameters
   * ```typescript
   * protected redirectToCartPage(): void {
   *   const currentLocale = this.getCurrentLocale();
   *   const cartUrl = currentLocale === '' ? '/cart' : `${currentLocale}/cart`;
   *
   *   // Add tracking parameters
   *   const trackedUrl = `${cartUrl}?source=boost_widget&timestamp=${Date.now()}`;
   *   this.safeRedirect(trackedUrl);
   * }
   * ```
   */
  protected redirectToCartPage(): void {
    const currentLocale = (
      (window as Window & { Shopify?: { routes?: { root?: string } } }).Shopify?.routes?.root ?? '/'
    ).replace(/\/$/, '');
    const cartUrl = currentLocale === '' ? '/cart' : `${currentLocale}/cart`;

    // Sanitize URL to prevent XSS attacks
    this.safeRedirect(cartUrl);
  }

  /**
   * Get current Shopify locale from window.Shopify object
   * Override to customize locale detection logic
   *
   * @returns Locale path (e.g., '', '/en', '/fr') without trailing slash
   */
  protected getCurrentLocale(): string {
    const shopifyRoot =
      (window as Window & { Shopify?: { routes?: { root?: string } } }).Shopify?.routes?.root ??
      '/';
    return shopifyRoot.replace(/\/$/, '');
  }

  /**
   * Safely redirect to URL with XSS protection
   * Validates URL is relative path before redirecting to prevent security vulnerabilities
   *
   * Security features:
   * - Only allows relative URLs starting with /
   * - Blocks protocol-relative URLs (//evil.com)
   * - Blocks javascript:, data:, and other protocol handlers
   * - Blocks absolute URLs to external sites
   *
   * @param url - URL to redirect to (must be relative path starting with /)
   * @protected
   *
   * @example
   * ```typescript
   * // Valid redirects
   * this.safeRedirect('/cart');
   * this.safeRedirect('/en/cart');
   * this.safeRedirect('/cart?source=widget');
   *
   * // Invalid redirects (will be blocked and logged)
   * this.safeRedirect('javascript:alert(1)'); // ❌ Protocol handler
   * this.safeRedirect('//evil.com'); // ❌ Protocol-relative
   * this.safeRedirect('https://evil.com'); // ❌ Absolute URL
   * ```
   */
  protected safeRedirect(url: string): void {
    // Only allow relative URLs starting with / (same-origin)
    // Prevent XSS via javascript:, data:, or absolute URLs to external sites
    if (!url.startsWith('/')) {
      this.appService.logger.error('Invalid redirect URL - must be relative path:', url);
      return;
    }

    // Additional check: prevent protocol-relative URLs (//evil.com)
    if (url.startsWith('//')) {
      this.appService.logger.error(
        'Invalid redirect URL - protocol-relative URLs not allowed:',
        url
      );
      return;
    }

    // Safe to redirect - URL is relative same-origin path
    window.location.href = url;
  }

  /**
   * Get all items currently in the cart
   * Returns empty array if cart is empty or on error
   *
   * @returns Promise resolving to array of cart items from Shopify (empty array on error)
   *
   * @example
   * ```typescript
   * const items = await cartService.getCartItems();
   * console.log(`Cart has ${items.length} items`);
   * ```
   */
  async getCartItems(): Promise<unknown[]> {
    try {
      return await this.cartAPI.getProductsInCart();
    } catch (error) {
      this.appService.logger.error('Failed to get cart items:', error);
      return [];
    }
  }

  /**
   * Get default cart rendering configuration
   * Override to provide shop-specific defaults that differ from standard settings
   *
   * Default values:
   * - cartStyle: 'side' (side drawer)
   * - showCurrencyCodes: false (hide currency codes)
   *
   * Common cart style values:
   * - 'side': Side drawer (slides from right)
   * - 'drawer': Bottom drawer
   * - 'modal': Center modal
   * - 'page': Full page redirect
   *
   * @returns CartRenderConfig object with default cartStyle and showCurrencyCodes settings
   *
   * @example Custom defaults for specific shop
   * ```typescript
   * protected getDefaultCartConfig(): CartRenderConfig {
   *   const shopConfig = this.appService.getConfig();
   *
   *   // Different defaults for multi-currency shops
   *   if (shopConfig.multiCurrency) {
   *     return {
   *       cartStyle: 'modal',
   *       showCurrencyCodes: true, // Show codes for clarity
   *     };
   *   }
   *
   *   return super.getDefaultCartConfig();
   * }
   * ```
   */
  protected getDefaultCartConfig(): CartRenderConfig {
    return {
      cartStyle: 'side',
      showCurrencyCodes: false,
    };
  }

  /**
   * Process and format cart item for display
   * Override to add shop-specific fields, transformations, or enrichments
   *
   * Default processing:
   * - Formats price using shop currency settings
   * - Filters out default/empty variant options
   * - Maintains all original item properties
   *
   * Common customization scenarios:
   * - Add product badges (new, sale, limited)
   * - Include inventory status
   * - Add estimated delivery dates
   * - Format custom properties for display
   * - Add product recommendations
   * - Include subscription information
   *
   * @param item - Raw cart item from Shopify Cart API
   * @param showCurrencyCodes - Whether to include currency codes in formatted price strings
   * @returns Processed cart item object with formatted fields ready for template rendering
   *
   * @example Add inventory status
   * ```typescript
   * protected processCartItem(item: Product, showCurrencyCodes: boolean) {
   *   const processed = super.processCartItem(item, showCurrencyCodes);
   *
   *   // Add inventory status
   *   const inventory = this.getInventoryStatus(item.variant_id);
   *   processed.inventoryStatus = {
   *     available: inventory.quantity > 0,
   *     quantity: inventory.quantity,
   *     message: this.getInventoryMessage(inventory),
   *   };
   *
   *   return processed;
   * }
   * ```
   *
   * @example Add subscription details
   * ```typescript
   * protected processCartItem(item: Product, showCurrencyCodes: boolean) {
   *   const processed = super.processCartItem(item, showCurrencyCodes);
   *
   *   // Add subscription information
   *   if (item.selling_plan_allocation) {
   *     processed.subscription = {
   *       active: true,
   *       frequency: item.selling_plan_allocation.selling_plan.name,
   *       discount: this.calculateSubscriptionDiscount(item),
   *     };
   *   }
   *
   *   return processed;
   * }
   * ```
   *
   * @example Format custom properties
   * ```typescript
   * protected processCartItem(item: Product, showCurrencyCodes: boolean) {
   *   const processed = super.processCartItem(item, showCurrencyCodes);
   *
   *   // Format gift message and wrapping
   *   if (item.properties) {
   *     processed.giftOptions = {
   *       hasGiftWrap: Boolean(item.properties._gift_wrap),
   *       message: item.properties._gift_message,
   *       from: item.properties._gift_from,
   *       to: item.properties._gift_to,
   *     };
   *   }
   *
   *   return processed;
   * }
   * ```
   */
  protected processCartItem(item: Product, showCurrencyCodes: boolean): Record<string, unknown> {
    const price = (item.final_line_price ?? item.line_price ?? 0) / 100;

    const priceStr = this.appService.formatCurrency(price, {
      showCurrencyCodes,
    });

    // Format selling plan allocation prices for pre-order items (cents → formatted currency)
    this.formatSellingPlanAllocation(item, showCurrencyCodes);

    return {
      ...item,
      price: priceStr,
      options_with_values: item.options_with_values?.filter((option) => {
        return option.name !== 'Title' && option.value !== 'Default Title';
      }),
    };
  }

  /**
   * Format selling plan allocation prices from raw cents to formatted currency strings.
   * Mutates the item in place, matching the deprecated `updateSellingPlanAllocation` behavior.
   *
   * This is a cart-local implementation that mirrors PreOrderService.formatSellingPlanAllocation
   * without importing it, to avoid circular dependencies (PreOrderModule lazy-imports CartModule).
   *
   * @param item - Cart item that may contain a selling_plan_allocation with numeric prices
   * @param showCurrencyCodes - Whether to include currency codes in formatted price strings
   */
  protected formatSellingPlanAllocation(item: Product, showCurrencyCodes: boolean): void {
    const cartItem = item as unknown as Record<string, unknown>;
    const allocation = cartItem.selling_plan_allocation as
      | { price?: string | number; compare_at_price?: string | number }
      | undefined;

    if (!allocation) return;

    allocation.price = this.appService.formatCurrency((Number(allocation.price) || 0) / 100, {
      showCurrencyCodes,
    });
    allocation.compare_at_price = this.appService.formatCurrency(
      (Number(allocation.compare_at_price) || 0) / 100,
      { showCurrencyCodes }
    );
  }

  /**
   * Render cart modal HTML with current cart data
   * Fetches cart data, applies formatting, and renders using cart template
   *
   * Customization points:
   * - {@link getDefaultCartConfig} - Default rendering settings
   * - {@link processCartItem} - Item formatting and enrichment
   * - {@link getCartTemplate} - Template source
   *
   * @returns Promise resolving to CartModalData with rendered HTML and item count
   *
   * @example Basic usage
   * ```typescript
   * const { html, itemCount } = await cartService.renderCartModal();
   * document.getElementById('cart-modal').innerHTML = html;
   * ```
   *
   * @example With custom rendering
   * ```typescript
   * class CustomCartService extends CartService {
   *   async renderCartModal() {
   *     const { html, itemCount } = await super.renderCartModal();
   *
   *     // Add recommendations to modal
   *     const recommendations = await this.getRecommendations();
   *     const enrichedHtml = this.injectRecommendations(html, recommendations);
   *
   *     return { html: enrichedHtml, itemCount };
   *   }
   * }
   * ```
   */
  async renderCartModal(): Promise<CartModalData> {
    const cartData = await this.cartAPI.getAll();
    const template = await this.getCartTemplate();

    const defaults = this.getDefaultCartConfig();
    let cartStyle = defaults.cartStyle;
    let showCurrencyCodes = defaults.showCurrencyCodes;

    const themeSettings = this.appService.themeSettings;
    cartStyle = themeSettings.cart.cartStyle ?? defaults.cartStyle;
    showCurrencyCodes = themeSettings.cart.showCurrencyCodes ?? defaults.showCurrencyCodes;

    const formattedItems = cartData.items.map((item: Product) =>
      this.processCartItem(item, showCurrencyCodes)
    );

    const formattedSubtotal = this.appService.formatCurrency(
      (cartData?.items_subtotal_price ?? 0) / 100,
      {
        showCurrencyCodes,
      }
    );

    // Get cart drawer recommendation widget data if enabled
    const recommendationData = await this.getCartDrawerRecommendationData(showCurrencyCodes);

    const html = await this.appService.templateRender(template, {
      items: formattedItems,
      item_count: cartData.item_count,
      items_subtotal_price: formattedSubtotal,
      cartStyle,
      locale: (window?.Shopify?.routes?.root ?? '/').replace(/\/$/, ''),
      translations: {},
      themeSettings,
      // Recommendation widget data for cart drawer template
      ...recommendationData,
    });

    return {
      html,
      itemCount: cartData.item_count ?? 0,
    };
  }

  /**
   * Get recommendation widget data for cart drawer template rendering.
   *
   * Delegates to CartDrawerRecommendationService for data fetching and formatting.
   * The service handles: validation, API calls, price formatting.
   *
   * Override this method in CartService for cart-specific customizations.
   * For recommendation-specific customizations, extend CartDrawerRecommendationService.
   *
   * @param showCurrencyCodes - Whether to show currency codes in formatted prices
   * @returns Promise resolving to object containing widget data, or empty object if disabled
   *
   * @example Override to add cart-specific data
   * ```typescript
   * protected async getCartDrawerRecommendationData(showCurrencyCodes: boolean) {
   *   const data = await super.getCartDrawerRecommendationData(showCurrencyCodes);
   *   if (data.products) {
   *     // Filter out products already in cart
   *     const cartProductIds = await this.getCartProductIds();
   *     data.products = data.products.filter(p => !cartProductIds.includes(p.id));
   *   }
   *   return data;
   * }
   * ```
   */
  protected async getCartDrawerRecommendationData(
    showCurrencyCodes: boolean
  ): Promise<CartDrawerRecommendationTemplateData | Record<string, never>> {
    // Delegate to CartDrawerRecommendationService
    const data =
      await this.cartDrawerRecommendationService.getCartDrawerRecommendationData(showCurrencyCodes);

    // Return empty object if no data (not enabled or error)
    if (!data) {
      return {};
    }

    return data;
  }

  /**
   * Get cart template HTML for rendering
   * Override to provide shop-specific templates or custom template loading logic
   *
   * Template resolution priority:
   * 1. TAEAppConfig.template.cart (from backend/metafield) - highest priority
   * 2. Local static template file (fallback) - default
   *
   * The template should use Liquid-compatible syntax and expect these variables:
   * - items: Array of formatted cart items
   * - item_count: Total number of items
   * - items_subtotal_price: Formatted subtotal price
   * - cartStyle: Cart display style ('side', 'modal', etc.)
   * - locale: Current locale path
   * - translations: Translation strings
   *
   * @returns Promise resolving to cart template HTML string
   *
   * @example Load template from custom location
   * ```typescript
   * protected async getCartTemplate(): Promise<string> {
   *   // Try shop-specific template first
   *   const shopId = this.appService.getConfig().shopId;
   *   const customTemplate = await this.loadCustomTemplate(shopId);
   *
   *   if (customTemplate) {
   *     return customTemplate;
   *   }
   *
   *   // Fallback to default
   *   return super.getCartTemplate();
   * }
   * ```
   *
   * @example Template with custom structure
   * ```typescript
   * protected async getCartTemplate(): Promise<string> {
   *   const baseTemplate = await super.getCartTemplate();
   *
   *   // Add custom sections to template
   *   const shopConfig = this.appService.getConfig();
   *
   *   if (shopConfig.showRecommendations) {
   *     return this.injectRecommendationsSection(baseTemplate);
   *   }
   *
   *   if (shopConfig.showLoyaltyPoints) {
   *     return this.injectLoyaltySection(baseTemplate);
   *   }
   *
   *   return baseTemplate;
   * }
   * ```
   */
  protected async getCartTemplate(): Promise<string> {
    // Try to get template from TAEAppConfig first
    const templateFromTAE = this.appService.TAEAppConfig.template?.cart;
    if (templateFromTAE) {
      return templateFromTAE;
    }

    // Fallback to local template if not in config
    const { cartTemplate } = await import('../../template/cart-template.js');
    return cartTemplate;
  }

  /**
   * Get current cart data
   * Business logic method - handles data retrieval and error handling
   *
   * @returns Promise resolving to cart data from Shopify Cart API
   * @throws {Error} When cart API request fails
   */
  async getCartData(): Promise<unknown> {
    try {
      return await this.cartAPI.getAll();
    } catch (error) {
      this.appService.logger.error('Error getting cart data:', error);
      throw error;
    }
  }

  /**
   * Change quantity of a cart item with queue management
   * Business logic method - ensures sequential updates to prevent conflicts
   *
   * @param productId - Product variant ID or cart line item ID
   * @param quantity - New quantity (0 to remove item)
   * @returns Promise resolving to cart update response from Shopify API
   * @throws {Error} When cart update fails or product ID is invalid
   */
  async changeQuantity(productId: string, quantity: number): Promise<unknown> {
    this.updateQueue = this.updateQueue
      .then(async () => {
        try {
          const response = await this.cartAPI.change(productId, quantity);

          const cartData = await this.cartAPI.getAll();
          this.cartSelectors.updateCartCount(cartData.item_count ?? 0);

          return response;
        } catch (error) {
          this.appService.logger.error('Error changing quantity:', error);
          throw error;
        }
      })
      .catch((error) => {
        // Reset queue on error to prevent cascade failures
        this.updateQueue = Promise.resolve();
        // Re-throw to allow caller to handle
        throw error;
      });

    return this.updateQueue;
  }

  /**
   * Update cart icon count in UI
   * Business logic method - updates UI elements with cart count
   *
   * @param count - Number of items in cart
   */
  updateCartIconCount(count: number): void {
    this.cartSelectors.updateCartCount(count);
  }

  /**
   * Restore cart from localStorage backup if backup exists
   * Handles cart recovery scenarios (after checkout, session restore, etc.)
   *
   * Process:
   * 1. Check for backup_cart_items in localStorage
   * 2. If found, clear current cart
   * 3. Add all backed up items back to cart
   * 4. Clean up backup (success or failure)
   *
   * Override to customize backup/restore logic or add validation
   *
   * @returns Promise resolving when restore is complete (or no backup found)
   *
   * @example Add validation before restore
   * ```typescript
   * async restoreCartIfNeeded(): Promise<void> {
   *   const backup = localStorage.getItem('backup_cart_items');
   *   if (!backup) return;
   *
   *   try {
   *     const items = JSON.parse(backup);
   *
   *     // Validate items are still available
   *     const validItems = await this.validateBackupItems(items);
   *
   *     if (validItems.length === 0) {
   *       this.showMessage('Backed up items are no longer available');
   *       return;
   *     }
   *
   *     await this.cartAPI.clearCart();
   *     await this.cartAPI.addMultiProductToCart(validItems);
   *
   *     this.showMessage(`Restored ${validItems.length} items to cart`);
   *   } catch (error) {
   *     this.appService.logger.error('Failed to restore cart:', error);
   *   } finally {
   *     localStorage.removeItem('backup_cart_items');
   *   }
   * }
   * ```
   *
   * @returns Promise that resolves when restore is complete or no backup exists
   */
  async restoreCartIfNeeded(): Promise<void> {
    const backup = localStorage.getItem('backup_cart_items');

    if (!backup) {
      return;
    }

    try {
      const items = JSON.parse(backup) as CartAddItem[];

      await this.cartAPI.clearCart();
      await this.cartAPI.addMultiProductToCart(items);

      localStorage.removeItem('backup_cart_items');
    } catch (error) {
      this.appService.logger.error('Failed to restore cart:', error);
      // Clean up backup even on failure to prevent repeated failed attempts
      localStorage.removeItem('backup_cart_items');
    }
  }
}
